'use client';

import { PortableText } from 'next-sanity';
import { useRef, useState, useEffect } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from 'framer-motion';

import heroBackground from '@/public/home/hero.jpg';
import heroBackgroundMobile from '@/public/home/hero-mobile.png';
import Image from 'next/image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableTextBlock } from 'sanity';
import Countdown from '../global/Countdown';
import { GROQButton } from '@/sanity/lib/definitions';
import { CTAButton } from '@/shadcn/ui/button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import VideoAsset from '@/components/utils/VideoAsset';
import { IMuxVideoPlayer } from '@/sanity/globals/VideoPlayer';
import { PlayVideoCursorFollower } from '../global/PlayVideoCursorFollower';
import { VideoControlsMinimal } from './VideoControlsMinimal';
import MuxHLSVideo from '@/components/utils/MuxHSLVideo';
import ScrollIndicator from '@/components/global/ui/ScrollIndicator';
import { useRouter } from 'next/navigation';
import { getCountdownTargetMs } from '@/lib/countdown';

interface IHeroProps {
  title: PortableTextBlock[];
  countdownTitle: string;
  numberOfPieces: string;
  countdownDescription: PortableTextBlock[];
  countdownTime: string;
  buttonOne: GROQButton;
  buttonTwo: GROQButton;
  enableCountdown?: boolean;
  useVideo?: boolean;
  backgroundVideo?: IMuxVideoPlayer;
  backgroundVideoMobile?: IMuxVideoPlayer;
}

export default function Hero({
  title,
  countdownTitle,
  numberOfPieces,
  countdownDescription,
  countdownTime,
  buttonOne,
  buttonTwo,
  enableCountdown,
  useVideo,
  backgroundVideo,
  backgroundVideoMobile,
}: IHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLVideoElement | null>(null);
  const modalRef = useRef<HTMLVideoElement | null>(null);
  const isMobile = useIsMobile();
  const router = useRouter();

  const [open, setOpen] = useState(false);

  // When the countdown reaches 00:00:00:00 (the launch moment), send visitors to
  // the sales page. Uses the same helper the Countdown displays, so it stays in
  // sync with the on-screen timer.
  useEffect(() => {
    if (!enableCountdown) return;
    const targetMs = getCountdownTargetMs(countdownTime);
    if (targetMs == null) return;

    let redirected = false;
    const check = () => {
      if (redirected) return;
      if (Date.now() >= targetMs) {
        redirected = true;
        router.replace('/princeptor');
      }
    };

    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, [enableCountdown, countdownTime, router]);
  // On mobile, use the dedicated mobile video (different aspect ratio) when available,
  // falling back to the desktop video otherwise.
  const playbackId =
    (isMobile
      ? backgroundVideoMobile?.muxVideo?.playbackId
      : backgroundVideo?.muxVideo?.playbackId) ??
    backgroundVideo?.muxVideo?.playbackId;

  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Transform scroll progress to translateY
  // Start parallax effect only after 15% scroll (0.15)
  // Map from 0.15-1 scroll progress to 0-200px translateY
  const imageY = useTransform(scrollYProgress, [0, 0.15, 1], [0, 0, 500]);

  const ease = [0.76, 0, 0.24, 1];

  const openOverlay = () => {
    const mv = modalRef.current;
    if (mv) {
      try {
        mv.muted = false;
        mv.volume = 1;
        mv.removeAttribute('muted');
        mv.currentTime = 0;
      } catch {}
    }
    setOpen(true);
  };

  const closeOverlay = () => {
    const mv = modalRef.current;
    if (mv) {
      try {
        mv.pause();
        mv.muted = true;
        mv.volume = 0;
        mv.removeAttribute('controls');
        mv.currentTime = 0;
      } catch {}
    }
    setOpen(false);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="flex flex-col items-center"
    >
      <div className="relative flex justify-between flex-col min-h-[145rem] lg:min-h-[115.125rem] w-full  overflow-clip px-[2rem] lg:px-[9rem] pt-[8rem] pb-0 lg:py-[10rem] lg:items-start items-center">
        <motion.div
          className="flex relative items-center z-10 lg:hidden flex-col gap-[3rem] mt-[7rem]"
          variants={itemVariants}
        >
          <p className="flex text-[1.5rem] text-offwhite font-nunito uppercase tracking-[0.5rem] font-[300] items-center gap-[1.5rem]">
            <span className="block">{countdownTitle}</span>
            <span className="w-[.5rem] h-[0.5rem] bg-offwhite rounded-full"></span>
            <span className="block">{numberOfPieces}</span>
          </p>

          {enableCountdown && <Countdown enableDays date={countdownTime} />}
        </motion.div>

        <div
          className={cn(
            'mt-auto w-full lg:gap-0 gap-[4rem] relative z-10 flex flex-wrap items-center lg:items-end justify-between',
            useVideo && playbackId && 'relative z-20 pointer-events-none',
          )}
        >
          <div className="flex flex-col-reverse lg:flex-col items-center lg:items-start gap-[3rem] lg:gap-[6rem]">
            <motion.div
              className="uppercase text-offwhite max-w-[87rem] pointer-events-none"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              variants={containerVariants}
            >
              {title && (
                <motion.div
                  className="flex prose-p:lg:text-[2rem] prose-p:text-[1.75rem] lg:text-start text-center flex-col gap-[2rem] lg:gap-[4rem] lg:prose-h1:leading-[110%] prose-h1:leading-[100%] pointer-events-auto"
                  variants={itemVariants}
                >
                  <PortableText value={title} components={richTextComponent} />
                </motion.div>
              )}

            </motion.div>

            <ScrollIndicator />
          </div>

          <motion.div
            className="lg:w-[67rem] lg:items-start items-center flex flex-col gap-[6rem] lg:gap-[5rem]"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
          >
            <motion.div
              className="hidden lg:flex flex-col gap-[3rem]"
              variants={itemVariants}
            >
              <p
                className={cn(
                  'flex text-[1.5rem] text-offwhite font-nunito uppercase tracking-[0.5rem] font-[300] items-center gap-[1.5rem] pointer-events-auto',
                  !enableCountdown &&
                    'bg-white/[8%] backdrop-blur-[1rem] border-gradient rounded-[1rem] px-[2.5rem] py-[1.5rem]',
                )}
              >
                <span className="block">{countdownTitle}</span>
                <span className="w-[.5rem] h-[0.5rem] bg-offwhite rounded-full"></span>
                <span className="block">{numberOfPieces}</span>
              </p>

              {enableCountdown && <Countdown enableDays date={countdownTime} />}
            </motion.div>

            <motion.div
              className="text-offwhite/70 lg:text-start text-center flex flex-col gap-[2rem] lg:gap-[3rem] lg:w-full w-[45rem] pointer-events-auto"
              variants={itemVariants}
            >
              {countdownDescription && (
                <PortableText
                  value={countdownDescription}
                  components={richTextComponent}
                />
              )}
            </motion.div>

            <motion.div
              className="flex flex-wrap items-center gap-[1.5rem] relative pointer-events-auto"
              variants={itemVariants}
            >
              {buttonOne && <CTAButton data={buttonOne} />}
              {buttonTwo && <CTAButton data={buttonTwo} />}
            </motion.div>
          </motion.div>
        </div>

        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
          }}
          className="absolute lg:w-[85rem] w-full opacity-[0.7] h-1/2 lg:h-full left-0  z-[1] pointer-events-none select-none bottom-0 lg:block hidden"
        ></div>

        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
          }}
          className="absolute lg:w-[85rem] w-full opacity-[0.7] h-1/2 lg:h-full right-0  z-[1] pointer-events-none select-none rotate-180  top-0 lg:block hidden"
        ></div>

        {useVideo && playbackId && (
          <>
            <div className="absolute inset-0 bottom-0 left-0 max-h-[105rem] justify-center items-center gap-[1.25rem] z-10 lg:hidden flex mix-blend-difference pointer-events-none">
              <button
                type="button"
                aria-label="Open overlay"
                onClick={openOverlay}
                className="pointer-events-auto"
              >
                <div className="py-[3rem] pl-[3.25rem] pr-[2.88rem] border border-white/5 bg-black/[35%] rounded-full backdrop-blur-[1.25rem]">
                  <svg
                    className="w-[2.59813rem] h-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="21"
                    viewBox="0 0 18 21"
                    fill="none"
                  >
                    <path
                      d="M18 10.3923L-9.08524e-07 20.7846L0 -7.86805e-07L18 10.3923Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </button>
            </div>

            <PlayVideoCursorFollower
              open={open}
              // @ts-ignore
              containerRef={sectionRef}
              label={open ? 'Close Video' : 'Play Video'}
            />

            <button
              type="button"
              aria-label="Open overlay"
              className="absolute inset-0 z-10 pointer-events-none lg:pointer-events-auto"
              onClick={openOverlay}
            />
          </>
        )}

        <motion.div
          className={cn(
            'absolute w-full lg:h-full inset-0 z-0 pointer-events-none select-none',
            enableCountdown === false ? 'h-[93rem]' : 'h-[110rem]',
          )}
          style={{ y: isMobile ? 0 : imageY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1.2,
            ease: [0.76, 0, 0.24, 1],
          }}
        >
          <div className="absolute lg:hidden block w-[calc(100%+2rem)] left-1/2 -translate-x-1/2 h-[60%] bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10"></div>

          {useVideo && playbackId ? (
            <VideoAsset
              loadOffset={1000}
              playOffset={300}
              ref={bgRef}
              playbackId={playbackId}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover absolute inset-0"
            />
          ) : (
            <Image
              src={isMobile ? heroBackgroundMobile : heroBackground}
              alt="Hero Background"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
            />
          )}
        </motion.div>

        <AnimatePresence>
          {open && useVideo && playbackId && (
            <motion.div
              key="overlay"
              className="fixed inset-0 z-[60] cursor-default flex items-center justify-center"
              style={{
                willChange: 'clip-path',
                WebkitClipPath: 'inset(50% 50% 50% 50%)',
              }}
              initial={{ clipPath: 'inset(100% 100% 100% 100%)' }}
              animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
              exit={{
                clipPath: 'inset(100% 100% 100% 100%)',
                transition: { duration: 2, ease },
              }}
              transition={{ duration: 1, ease }}
              aria-hidden
            >
              <motion.div
                className="absolute w-full h-full inset-0 overflow-clip bg-black"
                style={{
                  willChange: 'clip-path',
                  WebkitClipPath: 'inset(50% 50% 50% 50%)',
                }}
                initial={{ clipPath: 'inset(100% 100% 100% 100%)' }}
                animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                exit={{
                  clipPath: 'inset(100% 100% 100% 100%)',
                  transition: { duration: 2, ease, delay: 0.1 },
                }}
                transition={{ duration: 1, ease, delay: 0.1, stiffness: 50 }}
              >
                <VideoControlsMinimal videoRef={modalRef} />

                <button
                  className="lg:hidden block absolute z-[2] top-[3.25rem] right-[3.25rem]"
                  aria-label="Close"
                  onClick={closeOverlay}
                >
                  <svg
                    className="w-[3.2rem] h-[3.2rem]"
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 28 28"
                  >
                    <path
                      stroke="#fff"
                      strokeWidth="1.084"
                      d="m1 1 26.062 26.062M1 27.063 27.062 1"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  aria-label="Close"
                  onClick={closeOverlay}
                  className="absolute inset-0 z-[2] cursor-pointer lg:block hidden"
                />

                <div className="lg:w-full lg:h-full">
                  <MuxHLSVideo
                    ref={modalRef}
                    playbackId={playbackId}
                    className="absolute inset-0 w-full h-full lg:object-cover object-contain pointer-events-none video-no-native"
                    autoPlay
                    muted
                    preload="auto"
                    loop
                    playsInline
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
